import { Input, notification, Select, Tooltip } from "antd";
import classNames from "classnames";
import SVG from "components/common/SVG";
import { clientRoles, clientRolesForWorkspaceAdmin, ENTITY_TYPE, ROLE, roleLabels, userRoles } from "constants/phase";
import { useCallback, useEffect, useMemo, useState } from "react";
import Dropzone from "react-dropzone";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";

import PasswordFieldIco from "../../../../components/icons/PasswordShow";
import PasswordView from "../../../../components/icons/PasswordView";
import { getEntityType } from "../../../../helpers";
import {
  createOrganizationUser,
  getAllOrganizations,
  getAllOrganizationWorkspaces,
} from "../../../../store/organization/duck";
import { getUploadPicture } from "../../../../store/videogrid/duck";
import { createWorkspaceUser, getWorkspaceDetails } from "../../../../store/workspace/duck";
import CustomModal from "../CustomModal";
import styles from "./style.module.scss";

const mapDispatchToProps = {
  getUploadPicture,
  getAllOrganizations,
  getAllOrganizationWorkspaces,
  createOrganizationUser,
  createWorkspaceUser,
};

const AddUserModal = ({
  getUploadPicture,
  getAllOrganizations,
  showModal,
  entity,
  entityType,
  setShowModal,
  getAllOrganizationWorkspaces,
  createOrganizationUser,
  createWorkspaceUser,
  closeAndFetchModal,
  isAdmin,
}) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const [roleState, setRoleState] = useState(null);
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [workspaceOptions, setWorkspaceOptions] = useState([]);
  const [passwordType, setPasswordType] = useState(true);
  const { currentRole } = useOutletContext();
  const allowableRoles = useMemo(() => {
    if (getEntityType(id) === ENTITY_TYPE.WORKSPACES && isAdmin) {
      return Object.values(clientRolesForWorkspaceAdmin).map((role) => ({ value: role, label: roleLabels[role] }));
    }

    if (getEntityType(id) === ENTITY_TYPE.WORKSPACES) {
      return Object.values(clientRoles).map((role) => ({ value: role, label: roleLabels[role] }));
    } else if (currentRole == ROLE.ADMIN) {
      return Object.values(userRoles)
        .filter((data) => data !== ROLE.ADMIN)
        .map((role) => ({ value: role, label: roleLabels[role] }));
    } else {
      return Object.values(userRoles).map((role) => ({ value: role, label: roleLabels[role] }));
    }
  }, [entityType]);
  const togglePassword = () => {
    setPasswordType(!passwordType);
  };
  const handleClose = () => {
    setShowModal(null);
    closeAndFetchModal?.();
    setName("");
    setEmail("");
    setProfilePicture("");
    setSelectedOrganizationId(null);
    setSelectedWorkspaceId(null);
    setRoleState("");
  };

  const handleOrganizationSelect = async (organizationId) => {
    if (organizationId) {
      setSelectedOrganizationId(organizationId);
      await fetchOrganizationWorkspaces(organizationId);
      setSelectedWorkspaceId(null);
    } else {
      setWorkspaceOptions([]);
      setSelectedOrganizationId(null);
      setSelectedWorkspaceId(null);
    }
  };

  const handelAddUser = async () => {
    try {
      setLoading(true);

      if (selectedWorkspaceId) {
        const payload = {
          name,
          email,
          password,
          role: roleState,
          photo: profilePicture,
          workspaceId: selectedWorkspaceId,
        };
        const response = await createWorkspaceUser(payload);

        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      } else {
        const payload = {
          name,
          email,
          password,
          role: roleState,
          photo: profilePicture,
          organizationId: selectedOrganizationId,
        };
        const response = await createOrganizationUser(payload);

        if (response?.value?.status) {
          notification.success({ message: response?.value?.message });
        }
      }

      handleClose();
    } catch (error) {
      if (error.message) {
        notification.error({ message: error.message });
      }
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadFileAndGetUrl = async (data, type) => {
    setLoading(true);
    if (type !== "image") {
      notification["warning"]({ message: "Wrong file uploaded!" });
    } else {
      const { value } = await getUploadPicture(data);
      if (value.status) {
        setProfilePicture(value.data);
      }
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    const files = acceptedFiles;
    const file = files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = async () => {
          const data = new FormData();
          data.append("file", file);
          data.append("filename", file.name);
          let type = file?.type.split("/")[0];
          let title = file.name;
          await uploadFileAndGetUrl(data, type, title);
        };
      };
      reader.readAsDataURL(file);
    } else {
      notification["warning"]({ message: "Please select valid file!" });
    }
  };

  const fetchOrganizationWorkspaces = useCallback(
    async (organizationId, workspaceId = null) => {
      try {
        const response = await getAllOrganizationWorkspaces({ organizationId });
        if (response?.value?.workspaces) {
          setWorkspaceOptions(
            response.value.workspaces.map(({ workspaceId, displayName }) => ({
              value: workspaceId,
              label: displayName,
            })),
          );

          if (workspaceId && response.value.workspaces.find((workspace) => workspace.workspaceId === workspaceId)) {
            setSelectedWorkspaceId(workspaceId);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [getAllOrganizationWorkspaces],
  );

  const fetchAllOrganizations = useCallback(async () => {
    try {
      if (!isAdmin) {
        const response = await getAllOrganizations();

        if (response?.value?.organizations) {
          setOrganizationOptions(
            response.value.organizations.map(({ organizationId, displayName }) => ({
              value: organizationId,
              label: displayName,
            })),
          );

          if (entityType === ENTITY_TYPE.WORKSPACES) {
            setSelectedOrganizationId(entity?.organizationId);
            fetchOrganizationWorkspaces(entity?.organizationId, entity?.workspaceId);
          } else if (entity?.organizationId) {
            setSelectedOrganizationId(entity?.organizationId);
            fetchOrganizationWorkspaces(entity?.organizationId);
          }
        }
      } else {
        let workspaceDetail;
        if (getEntityType(id) === ENTITY_TYPE.WORKSPACES) {
          workspaceDetail = await getWorkspaceDetails({
            entityId: entity.workspaceId,
            getConfig: false,
          }).payload;
        }
        setOrganizationOptions([
          {
            value: entity?.organizationId || JSON.parse(JSON.stringify(workspaceDetail)).workspace.organizationId,
            label:
              getEntityType(id) === ENTITY_TYPE.WORKSPACES
                ? JSON.parse(JSON.stringify(workspaceDetail)).organization.displayName
                : entity?.name,
          },
        ]);

        setSelectedOrganizationId(
          entity?.organizationId || JSON.parse(JSON.stringify(workspaceDetail)).workspace.organizationId,
        );

        if (getEntityType(id) === ENTITY_TYPE.WORKSPACES && isAdmin) {
          setSelectedWorkspaceId(entity?.workspaceId);
          setWorkspaceOptions([{ value: entity?.workspaceId, label: entity?.name }]);
        } else {
          fetchOrganizationWorkspaces(
            entity?.organizationId || JSON.parse(JSON.stringify(workspaceDetail)).workspace.organizationId,
            entity?.workspaceId,
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [entity, entityType, fetchOrganizationWorkspaces, getAllOrganizations, isAdmin]);

  useEffect(() => {
    fetchAllOrganizations();
  }, [fetchAllOrganizations]);
  return (
    <>
      <CustomModal
        showModal={showModal}
        setShowModal={setShowModal}
        modalClassName={styles.modal}
        contentLabel="Add new user"
        primaryCtaText="Add"
        secondaryCtaText="Close"
        primaryCtaHandler={handelAddUser}
        secondaryCtaHandler={handleClose}
        primaryCtaEnabled={name && email && roleState && selectedOrganizationId && !loading}
      >
        <div className={styles.formContainer}>
          <div className={classNames(styles.formItem, styles.formProfilePictureItem)}>
            <div className={styles.profilePicturePreviewContainer}>
              {profilePicture ? (
                <img className={styles.profilePicturePreview} src={profilePicture} alt="profile-preview" />
              ) : (
                <SVG className={styles.profilePicturePreview} iconName={"images/preview-profile-picture.svg"} />
              )}
            </div>

            <Dropzone accept="image/*" multiple={false} onDrop={onDrop}>
              {({ getRootProps, getInputProps }) => (
                <div className={styles.profilePictureContainer} {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className={styles.profilePictureCta}>Upload picture</div>
                </div>
              )}
            </Dropzone>
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Name</div>
            <Input className={styles.textInput} value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Email</div>
            <Input className={styles.textInput} type="" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Password</div>
            <div className={styles.inputWithIcon}>
              <Input
                className={classNames(styles.textInput, styles.textInputIcon)}
                type={passwordType ? "password" : "text"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {passwordType ? (
                <PasswordFieldIco className={styles.TextInputImage} onClick={togglePassword} />
              ) : (
                // <img src={PasswordView} className="cursor-pointer" onClick={togglePassword} />
                <PasswordView className={styles.TextInputImage} onClick={togglePassword} />
              )}
            </div>
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Organizations</div>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              className="custom-ant-select-v2"
              value={selectedOrganizationId}
              disabled={!!entity}
              onChange={handleOrganizationSelect}
              options={organizationOptions}
            />
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Workspace</div>
            <div className={styles.workspaceSelectContainer}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                className="custom-ant-select-v2"
                disabled={!selectedOrganizationId || !!entity}
                value={selectedWorkspaceId} // we need 2 way binding here
                onChange={(workspaceId) => setSelectedWorkspaceId(workspaceId)}
                options={workspaceOptions}
              />

              <Tooltip placement="top" title={"If the workspace is empty, User will be added in organization."} arrow>
                <SVG className={styles.infoIcon} iconName={"images/info.svg"} />
              </Tooltip>
            </div>
          </div>

          <div className={styles.formItem}>
            <div className={styles.formItemLabel}>Role</div>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              disabled={!selectedOrganizationId}
              className="custom-ant-select-v2"
              onChange={(value) => {
                setRoleState(value);
              }}
              options={allowableRoles}
            />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default connect((/*state*/) => ({}), mapDispatchToProps)(AddUserModal);
